import { Box, Chip, Typography } from "@mui/material"
import { CloseFilledOutlined } from "@mui-symbols-material/w300"
import dayjs from "dayjs"
import type { FC } from "react"
import React, { useState } from "react"

import DisplayAS400Id from "@/components/common/DisplayAS400Id/DisplayAS400Id"
import { ConfirmationDialog } from "@/components/common/Modals"
import { DATE_FORMAT } from "@/constants/formats"
import type { Product } from "@/graphql/codegen/graphql"
import type { ProductTestWithDocuments } from "@/screens/Products/components/Product/Quality/TestResults/types"
import { useCurrentUserStore } from "@/stores/currentUserStore"

interface ProductTestInfoProps {
  product: Product
  shelfLifeMonths: number
  productTest: ProductTestWithDocuments | null
  poIds: number[]
  onRemovePo: (removedPoId: number) => void
}

export const ProductTestInfo: FC<ProductTestInfoProps> = ({
  product,
  shelfLifeMonths,
  productTest,
  poIds,
  onRemovePo,
}) => {
  const currentUser = useCurrentUserStore((state) => state.currentUser)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [orderIdToRemove, setOrderIdToRemove] = useState<number | null>(null)
  const createdOn = dayjs(productTest?.createdOn).format(DATE_FORMAT.US)
  const createdBy = productTest?.createdByUser?.name ?? currentUser?.name ?? ""

  const handlePoDelete = () => {
    setIsConfirmationModalOpen(false)
    setOrderIdToRemove(null)
    onRemovePo(orderIdToRemove!)
  }

  const handleDeletePoClick = (poId: number) => {
    setOrderIdToRemove(poId)
    setIsConfirmationModalOpen(true)
  }

  return (
    <Box className='text-gray-700'>
      <Typography variant='body1' component='div' className='mb-4 font-bold' data-testid='product-company-name'>
        {product.company?.name}
      </Typography>
      <Typography variant='body2' component='div' className='flex gap-x-8'>
        <Box>
          <Box className='font-bold'>Product ID</Box>
          <Box data-testid='product-id'>
            <Box className='mb-1'>{product.productId}</Box>
            <DisplayAS400Id id={product.externalSystemId} />
          </Box>
        </Box>
        <Box>
          <Box className='font-bold'>Product Shelf Life</Box>
          <Box data-testid='product-shelf-life'>{shelfLifeMonths} Months</Box>
        </Box>
        <Box>
          <Box className='font-bold'>Entered Date</Box>
          <Box data-testid='entered-date'>{createdOn}</Box>
        </Box>
        <Box>
          <Box className='font-bold'>Entered By</Box>
          <Box data-testid='entered-by'>{createdBy}</Box>
        </Box>
        <Box>
          <Box className='font-bold'>Associated PO#s</Box>
          <Box data-testid='associated-pos'>
            {poIds.map((poId) => (
              <Chip
                key={poId}
                label={poId ?? "-"}
                className='mb-2 mr-2 h-6 rounded-sm bg-gray-300 text-xs text-gray-900'
                onDelete={() => handleDeletePoClick(poId)}
                deleteIcon={<CloseFilledOutlined className='size-4 text-gray-900' />}
              />
            )) || "-"}
          </Box>
        </Box>
      </Typography>
      {isConfirmationModalOpen && (
        <ConfirmationDialog
          title='This action will remove the PO# from this test data.'
          content='Do you wish to proceed?'
          closeLabel='Cancel'
          confirmLabel='Yes, Remove'
          onConfirm={handlePoDelete}
          onClose={() => setIsConfirmationModalOpen(false)}
        />
      )}
    </Box>
  )
}
